import type { ReactNode } from 'react';
import classNames from 'clsx';
import type { Change } from 'diff';

import { buildCollapsibleDiffPlan, expandUnchangedLinesLabel, type ChangeGroup } from './diffCollapse';
import { diffElementClassNames } from './diffElementClassNames';
import { buildAddedLineWordDiffSpans, buildRemovedLineWordDiffSpans } from './diffWordSpans';
import type { ElementState } from './types';
import { elementDataAttributes } from './utils';

import styles from './Diff.module.css';

/**
 * Unified Diff Renderer
 *
 * This module provides utilities for rendering unified diffs with collapsible sections.
 * Grouping and collapse planning live in `./diffCollapse`.
 *
 * @fileoverview Utilities for rendering unified diffs with collapsible sections
 */

export type { ChangeGroup } from './diffCollapse';
export { groupChangesForCollapse } from './diffCollapse';

/**
 * Renders a diff with collapsible sections for unchanged content.
 */
export function renderCollapsibleDiff(
  groups: ChangeGroup[],
  expandedGroups: Set<number>,
  showLineNumbers: boolean,
  includeSubDiffs: boolean,
  contextLines: number,
  onToggleExpand: (groupIndex: number) => void,
): React.ReactNode[] {
  const elements: React.ReactNode[] = [];

  for (const step of buildCollapsibleDiffPlan(groups, expandedGroups, contextLines)) {
    if (step.kind === 'render') {
      elements.push(...renderDiff(step.changes, showLineNumbers, includeSubDiffs, [], step.startLine));
    } else {
      const label = expandUnchangedLinesLabel(step.hiddenCount, step.position);
      elements.push(
        <button
          key={`expand-${step.groupIndex}`}
          className={styles.expandSection}
          onClick={() => onToggleExpand(step.groupIndex)}
          type="button"
          aria-label={label}
        >
          <div className={styles.expandButton} data-testid="expand-unchanged-lines">
            <span className={styles.expandIcon}>⋯</span>
            <span className={styles.expandText}>{label}</span>
          </div>
        </button>,
      );
    }
  }

  return elements;
}

/**
 * Renders individual diff changes into React elements.
 */
export function renderDiff(
  diff: Change[],
  showLineNumbers: boolean,
  includeSubDiffs: boolean,
  elements: React.ReactNode[] = [],
  lineNumber: number = 1,
  prevItem: Change | null = null,
) {
  const [current, ...rest] = diff;
  const elementState: ElementState = current.added ? 'added' : current.removed ? 'removed' : 'unchanged';
  const next = rest[0];
  let innerElements: ReactNode[] = [current.value];

  if (current.added && prevItem?.removed && includeSubDiffs) {
    innerElements = buildAddedLineWordDiffSpans(prevItem.value, current.value, lineNumber);
  }

  if (current.removed && next?.added && includeSubDiffs) {
    innerElements = buildRemovedLineWordDiffSpans(current.value, next.value, lineNumber);
  }

  if (current.value.trim() === '') {
    innerElements = [<br key={`empty-${lineNumber}`} />];
  }

  const elementClassName = diffElementClassNames[elementState];
  const dataAttribute = elementDataAttributes[elementState];

  const ammendedElements = [
    ...elements,
    <div
      key={current.value + lineNumber}
      className={classNames(elementClassName, {
        [styles.showLineNumbers]: showLineNumbers,
      })}
      {...{ [`data-${dataAttribute}`]: true }}
    >
      {showLineNumbers && (
        <div data-line-number className={styles.lineNumber}>
          {lineNumber}
        </div>
      )}

      {showLineNumbers && elementState === 'unchanged' && (
        <div data-line-number className={`${styles.lineNumber} ${styles.lineNumberUnchanged}`}>
          {lineNumber}
        </div>
      )}

      {innerElements}
    </div>,
  ];

  if (!next) {
    return ammendedElements;
  }

  const nextLineNumber = current.removed && next.added ? lineNumber : lineNumber + 1;

  return renderDiff(rest, showLineNumbers, includeSubDiffs, ammendedElements, nextLineNumber, current);
}
